import React from 'react';

/**
 * Select with label, hint, error.
 *
 * VENDORED CHANGE: upstream has no `error` prop at all — it cannot show an
 * invalid state, which a form application needs (State is a required field).
 * Added error styling plus aria-invalid and aria-describedby, matching Input.
 * Belongs upstream.
 */
export function Select({ label, hint, error, options = [], size = 'md', placeholder, style, id, ...rest }) {
  const [focus, setFocus] = React.useState(false);
  const reactId = React.useId();
  const selId = id || reactId;
  const describedById = `${selId}-desc`;

  const heights = { sm: 36, md: 44, lg: 52 };
  const h = heights[size] || heights.md;
  const borderColor = error ? 'var(--y-danger)' : focus ? 'var(--accent-primary)' : 'var(--border-default)';
  const message = error || hint;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', ...style }}>
      {label && (
        <label
          htmlFor={selId}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)',
          }}
        >
          {label}
        </label>
      )}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <select
          id={selId}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={message ? describedById : undefined}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            appearance: 'none', WebkitAppearance: 'none', width: '100%', height: h,
            padding: '0 40px 0 var(--sp-4)', background: 'var(--bg-surface)',
            border: `1.5px solid ${borderColor}`,
            borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-body)', color: 'var(--text-strong)', cursor: 'pointer',
            outline: 'none',
            boxShadow: focus ? '0 0 0 3px var(--focus-ring)' : 'none',
            transition: 'border-color var(--dur) var(--ease-out), box-shadow var(--dur) var(--ease-out)',
          }}
          {...rest}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => {
            const val = typeof o === 'string' ? o : o.value;
            const lbl = typeof o === 'string' ? o : o.label;
            return (
              <option key={val} value={val}>
                {lbl}
              </option>
            );
          })}
        </select>
        <svg
          style={{ position: 'absolute', right: 14, pointerEvents: 'none', color: 'var(--text-muted)' }}
          width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {message && (
        <span
          id={describedById}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)',
            color: error ? 'var(--y-danger)' : 'var(--text-muted)',
          }}
        >
          {message}
        </span>
      )}
    </div>
  );
}
